import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ExamRoomList } from '@/types/scheduling-api';

/**
 * Practice exam-room list — drives the full column set for the By-Room
 * calendar view (PROD-985), INCLUDING rooms with zero appointments today.
 *
 * SOURCE: `GET /api/Practice/EditPracticeExamRoom/{practiceId}` — the same
 * endpoint Flutter's calendar screen hits via `FetchExamRoomListEvent`
 * (`settings_data_source_impl.dart:1165` → `calendar_screen.dart:2240`,
 * which reads `examRoomList[0].examRooms`). Flutter renders one By-Room
 * column per `ExamRoom`, sorted by name case-insensitively
 * (`calendar_by_room_widget.dart:94`); it does NOT derive columns from the
 * appointment payload. We mirror that: return the room NAMES, sorted, so
 * the caller can hand them to `<ByRoomCalendar rooms={...} />`.
 *
 * Base note: this is `/api/Practice` (NOT `/api/v1/Practice` — calendarSlots
 * is the v1 one). The Flutter enum is `PracticeService.Practice`
 * (`api_constants.dart:321`) vs `PracticeService.Practice_v1` for slots.
 *
 * Response is a LIST of `ExamRoomList`; Flutter reads index `[0]`. We do the
 * same, coalescing every missing layer to empty so a practice with no rooms
 * configured yields `[]` (the component then shows its empty state).
 */
/**
 * Pure extractor: `ExamRoomList[]` → sorted, deduped room NAMES. Flutter
 * reads index `[0].examRooms` and sorts by `examRoom.toLowerCase()`. We
 * additionally trim, drop blanks, and dedupe case-insensitively (room
 * names are user-entered — "Room 1" / "room 1" collapse to one column).
 * Exported for unit tests.
 */
export function extractExamRoomNames(
  res: ExamRoomList[] | null | undefined,
): string[] {
  const rooms = res?.[0]?.examRooms ?? [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const r of rooms) {
    const name = r.examRoom?.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  names.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  return names;
}

export function useExamRooms(practiceId: string | null | undefined) {
  return useQuery({
    queryKey: ['exam-rooms', practiceId],
    queryFn: async ({ signal }): Promise<string[]> => {
      if (!practiceId) throw new Error('useExamRooms called without practiceId');
      const res = await get<ExamRoomList[]>(
        `/api/Practice/EditPracticeExamRoom/${practiceId}`,
        { signal },
      );
      return extractExamRoomNames(res);
    },
    enabled: !!practiceId,
  });
}
